import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetApiRequest } from '../../services/api';

export const fetchAllExperiences = createAsyncThunk(
  'experiences/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, filters = {}, selectedGroup = '' } = params;
      
      const { stateFilter = 'all', importedOnly = false } = params;

      const response = await GetApiRequest('admin/experiences', {
        page,
        limit,
        category: filters.category || undefined,
        price: filters.price || undefined,
        search: filters.search || undefined,
        lat: filters.lat || undefined,
        lng: filters.lng || undefined,
        distance: filters.distance || undefined,
        date: filters.date || undefined,
        day: filters.day || undefined,
        time: filters.time || undefined,
        upcoming: filters.upcoming || undefined,
        thisWeekend: filters.thisWeekend || undefined,
        grouptype: selectedGroup || undefined,
        state: stateFilter && stateFilter !== 'all' ? stateFilter : undefined,
        importedOnly: importedOnly ? 'true' : undefined,
      });

      const data = response?.data?.data || {};
      const pagination = data.pagination || { page: 1, limit: 10, total: 0, pages: 1 };

      return {
        experiences: data.experiences || [],
        pagination: {
          pages: pagination.pages || 1,
          page: pagination.page || 1,
          total: pagination.total || 0
        }
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to load experiences');
    }
  }
);

const initialState = {
  experiences: [],
  loading: false,
  error: null,
  pagination: {
    pages: 1,
    page: 1,
    total: 0
  },
  currentPage: 1,
  limit: 10,
  filters: {
    category: "",
    price: "",
    search: "",
    lat: "",
    lng: "",
    distance: "",
    date: "",
    day: "",
    time: "",
    upcoming: false,
    thisWeekend: false,
  },
  selectedGroup: "",
  stateFilter: "all",
  importedOnly: false,
  hasLoaded: false,
  lastFetchedPage: null,
  lastFetchedLimit: null,
  lastFetchedFilters: null,
  lastFetchedSelectedGroup: null,
  lastFetchedStateFilter: null,
  lastFetchedImportedOnly: null,
};

const experiencesSlice = createSlice({
  name: 'experiences',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.currentPage = 1;
    },
    setSelectedGroup: (state, action) => {
      state.selectedGroup = action.payload;
      state.currentPage = 1;
    },
    setStateFilter: (state, action) => {
      state.stateFilter = action.payload;
      state.currentPage = 1;
    },
    setImportedOnly: (state, action) => {
      state.importedOnly = action.payload;
      state.currentPage = 1;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllExperiences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllExperiences.fulfilled, (state, action) => {
        state.loading = false;
        state.experiences = action.payload.experiences;
        state.pagination = action.payload.pagination;
        state.currentPage = action.payload?.pagination?.page || state.currentPage;
        state.hasLoaded = true;
        state.lastFetchedPage = action?.meta?.arg?.page ?? state.pagination.page;
        state.lastFetchedLimit = action?.meta?.arg?.limit ?? state.limit;
        state.lastFetchedFilters = action?.meta?.arg?.filters ?? state.filters;
        state.lastFetchedSelectedGroup = action?.meta?.arg?.selectedGroup ?? state.selectedGroup;
        state.lastFetchedStateFilter = action?.meta?.arg?.stateFilter ?? state.stateFilter;
        state.lastFetchedImportedOnly = action?.meta?.arg?.importedOnly ?? state.importedOnly;
      })
      .addCase(fetchAllExperiences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setCurrentPage, setFilters, setSelectedGroup, setStateFilter, setImportedOnly, clearError } = experiencesSlice.actions;
export default experiencesSlice.reducer;
